import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../shared/Icons';
import { formatDateWithWeekday, formatDateShort } from './community/utils';
import './Archive.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://community.ilouli.com';

// 뉴스 카테고리 한글 매핑
const NEWS_CATEGORY_MAP = {
  'headlines': '헤드라인',
  'economy': '경제',
  'finance': '금융',
  'stock': '증권/주식',
  'realestate': '부동산',
  'business': '비즈니스',
  'industry': '산업',
  'semiconductor': '반도체',
  'auto': '자동차',
  'shipbuilding': '조선',
  'startup': '스타트업',
  'environment': '환경',
  'sports': '스포츠',
  'drama': '드라마/연예',
  'game': '게임',
};

const getCategoryName = (category) => {
  return NEWS_CATEGORY_MAP[category] || category;
};

const Archive = ({ type = null }) => {
  const { t } = useTranslation();
  // type이 지정되면 해당 탭만 표시, 아니면 전체 표시
  const [activeTab, setActiveTab] = useState(type || 'news');
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [articles, setArticles] = useState([]);

  // 뉴스 아카이브 확장 상태
  const [newsViewMode, setNewsViewMode] = useState('list'); // 'list', 'category'
  const [newsCategories, setNewsCategories] = useState([]);
  const [expandedNewsCategory, setExpandedNewsCategory] = useState(null);

  // 브리핑 통합 데이터 (날짜별로 모든 브리핑 타입을 함께 표시)
  const [briefingData, setBriefingData] = useState({
    daily: null,
    themes: null,
    audioBriefings: []  // 아침/저녁 모두 저장
  });

  // 선택된 오디오 시간대
  const [selectedAudioSlot, setSelectedAudioSlot] = useState(null);

  // 오디오 플레이어 상태
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioTime, setAudioTime] = useState({ current: 0, duration: 0 });
  const audioRef = useRef(null);

  // 브리핑 내부 탭 (오디오, 일일, 테마)
  const [briefingViewTab, setBriefingViewTab] = useState('daily');

  // 검색 상태
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // 날짜 드롭다운 상태
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const dateDropdownRef = useRef(null);

  // type prop이 변경되면 activeTab 동기화
  useEffect(() => {
    if (type) {
      setActiveTab(type);
      clearSearch();
    }
  }, [type]);

  // 검색 초기화
  const clearSearch = () => {
    setSearchMode(false);
    setSearchQuery('');
    setSearchResults(null);
  };

  // 날짜 목록 로드
  const fetchDates = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'news':
          endpoint = '/api/news/archive/dates';
          break;
        case 'briefing':
          // 일일 브리핑 기준으로 날짜 목록 가져오기
          endpoint = '/api/news/briefing/archive';
          break;
        default:
          return;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`);
      const data = await response.json();

      if (data.success) {
        const loadedDates = data.archives || data.data || data.dates || [];
        setDates(loadedDates);
        // 최근 날짜 자동 선택 (첫 번째 항목이 가장 최신)
        if (loadedDates.length > 0) {
          setSelectedDate(loadedDates[0].date);
        }

      }
    } catch (err) {
      console.error('날짜 목록 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // 브리핑 상세 데이터 로드 (모든 타입 병렬 로드)
  const fetchBriefingDetail = useCallback(async (date) => {
    if (!date) return;

    // 모든 브리핑 타입을 병렬로 로드 (오디오는 모든 시간대 가져오기)
    const [dailyRes, themesRes, audioAllRes] = await Promise.allSettled([
      fetch(`${API_BASE_URL}/api/news/briefing/archive/${date}`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/news/briefing/archive/themes/${date}`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/news/briefing/archive/audio/${date}/all`).then(r => r.json())
    ]);

    // 오디오 브리핑 배열 처리
    let audioBriefings = [];
    if (audioAllRes.status === 'fulfilled' && audioAllRes.value.success && audioAllRes.value.briefings) {
      audioBriefings = audioAllRes.value.briefings;
    }

    setBriefingData({
      daily: dailyRes.status === 'fulfilled' && dailyRes.value.success ? dailyRes.value.briefing : null,
      themes: themesRes.status === 'fulfilled' && themesRes.value.success ? themesRes.value : null,
      audioBriefings: audioBriefings
    });

    // 첫 번째 오디오 선택 (없으면 기본값 morning)
    if (audioBriefings.length > 0) {
      setSelectedAudioSlot(audioBriefings[0].timeSlot || 'morning');
    } else {
      setSelectedAudioSlot('morning'); // 기본값으로 아침 선택
    }
  }, []);

  // 상세 데이터 로드
  const fetchDetail = useCallback(async (date) => {
    if (!date) return;
    setDetailLoading(true);

    try {
      if (activeTab === 'briefing') {
        // 브리핑은 모든 타입을 함께 로드
        await fetchBriefingDetail(date);
      } else {
        let endpoint = '';
        switch (activeTab) {
          case 'news':
            endpoint = `/api/news?date=${date}&limit=50`;
            break;
          default:
            return;
        }

        const response = await fetch(`${API_BASE_URL}${endpoint}`);
        const data = await response.json();

        if (data.success) {
          switch (activeTab) {
            case 'news':
              const newsArticles = data.articles || [];
              setArticles(newsArticles);
              // 카테고리별 그룹화
              const categoryMap = {};
              newsArticles.forEach(article => {
                const cat = article.category || '기타';
                if (!categoryMap[cat]) {
                  categoryMap[cat] = { category: cat, articles: [], count: 0 };
                }
                categoryMap[cat].articles.push(article);
                categoryMap[cat].count++;
              });
              const sortedCategories = Object.values(categoryMap)
                .sort((a, b) => b.count - a.count);
              setNewsCategories(sortedCategories);
              break;
            default:
              break;
          }
        }
      }
    } catch (err) {
      console.error('상세 데이터 로드 실패:', err);
    } finally {
      setDetailLoading(false);
    }
  }, [activeTab, fetchBriefingDetail]);

  useEffect(() => {
    fetchDates();
    setSelectedDate(null);
    setArticles([]);
    setBriefingData({ daily: null, themes: null, audioBriefings: [] });
    setSelectedAudioSlot(null);
    // 뉴스 관련 상태 초기화
    setNewsViewMode('list');
    setNewsCategories([]);
    setExpandedNewsCategory(null);
  }, [fetchDates, activeTab]);

  useEffect(() => {
    if (selectedDate) {
      fetchDetail(selectedDate);
    }
  }, [selectedDate, fetchDetail]);

  // 오디오 플레이어 컨트롤
  const handleAudioPlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setAudioTime({
        current: audioRef.current.currentTime,
        duration: audioRef.current.duration || 0
      });
    }
  };

  const handleAudioSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = percent * audioRef.current.duration;
    }
  };

  const formatAudioTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 검색 실행
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      alert('검색어는 2자 이상 입력해주세요.');
      return;
    }

    setSearching(true);
    setSearchResults(null);

    try {
      let endpoint = '';
      switch (activeTab) {
        case 'news':
        case 'briefing':
          endpoint = `/api/news/archive/search?q=${encodeURIComponent(searchQuery)}&type=${activeTab === 'news' ? 'news' : 'briefing'}`;
          break;
        default:
          return;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`);
      const data = await response.json();

      if (data.success) {
        setSearchResults(data.results);
        setSearchMode(true);
      }
    } catch (err) {
      console.error('검색 실패:', err);
      alert('검색 중 오류가 발생했습니다.');
    } finally {
      setSearching(false);
    }
  }, [activeTab, searchQuery]);

  // formatDate, formatDateShort: community/utils.js에서 import
  const formatDate = formatDateWithWeekday;

  // 탭 이름
  const tabNames = {
    news: '뉴스',
    briefing: 'AI 브리핑'
  };

  // 페이지 제목
  const getPageTitle = () => {
    if (type === 'news') return '뉴스 기록';
    if (type === 'briefing') return '브리핑 기록';
    return '지난 기록';
  };

  const getPageDesc = () => {
    if (type === 'news') return '날짜별 수집된 뉴스를 확인하세요';
    if (type === 'briefing') return '날짜별 AI 브리핑을 한눈에 확인하세요';
    return '날짜별로 수집된 뉴스, AI 브리핑을 확인하세요';
  };

  // 날짜 목록 아이템 카운트 표시
  const getDateItemCount = (item) => {
    if (activeTab === 'news') return `${item.articleCount || item.count}건`;
    if (activeTab === 'briefing') return `${item.summaryCount}개 요약`;
    return '';
  };

  // 현재 선택된 날짜의 인덱스
  const currentDateIndex = dates.findIndex(d => d.date === selectedDate);

  // 이전/다음 날짜로 이동
  const goToPrevDate = () => {
    if (currentDateIndex < dates.length - 1) {
      setSelectedDate(dates[currentDateIndex + 1].date);
    }
  };

  const goToNextDate = () => {
    if (currentDateIndex > 0) {
      setSelectedDate(dates[currentDateIndex - 1].date);
    }
  };

  // 날짜 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 브리핑 데이터 존재 여부 확인
  const hasBriefingData = briefingData.daily || briefingData.themes || briefingData.audioBriefings.length > 0;

  // 현재 선택된 오디오 브리핑
  const currentAudio = briefingData.audioBriefings.find(a => a.timeSlot === selectedAudioSlot) || briefingData.audioBriefings[0];

  return (
    <div className="archive-page">
      <div className="archive-header">
        <div className="header-top">
          <div>
            <h1>
              <Icon name="archive" size={28} />
              {getPageTitle()}
            </h1>
            <p>{getPageDesc()}</p>
          </div>
        </div>

        {/* 검색 섹션 */}
        <div className="search-section">
          <div className="search-input-wrapper">
            <Icon name="search" size={18} />
            <input
              type="text"
              placeholder="제목, 키워드로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            {searchQuery && (
              <button className="clear-btn" onClick={() => setSearchQuery('')}>×</button>
            )}
          </div>
          <button className="search-btn" onClick={handleSearch} disabled={searching}>
            {searching ? '검색 중...' : '검색'}
          </button>
          {searchMode && (
            <button className="reset-btn" onClick={clearSearch}>
              검색 초기화
            </button>
          )}
        </div>
      </div>

      {/* 탭 메뉴 - type이 지정되지 않았을 때만 표시 */}
      {!type && (
        <div className="archive-tabs">
          {Object.entries(tabNames).map(([key, name]) => (
            <button
              key={key}
              className={`tab-btn ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      <div className="archive-content">
        {/* 검색 결과 모드 */}
        {searchMode && searchResults ? (
          <div className="search-results">
            <h3>"{searchQuery}" 검색 결과</h3>

            {/* 뉴스 검색 결과 */}
            {activeTab === 'news' && searchResults.news && (
              <div className="result-section">
                <p className="result-count">{searchResults.news.length}개의 뉴스</p>
                {searchResults.news.length === 0 ? (
                  <p className="empty">검색 결과가 없습니다</p>
                ) : (
                  <ul className="article-list">
                    {searchResults.news.map((article) => (
                      <li key={article.id} className="article-item">
                        <a href={article.link} target="_blank" rel="noopener noreferrer">
                          <span className="category">{getCategoryName(article.category)}</span>
                          <span className="title">{article.title}</span>
                          <span className="source">{article.source}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* 브리핑 검색 결과 */}
            {activeTab === 'briefing' && searchResults.briefing && (
              <div className="result-section">
                <p className="result-count">{searchResults.briefing.length}개의 브리핑</p>
                {searchResults.briefing.length === 0 ? (
                  <p className="empty">검색 결과가 없습니다</p>
                ) : (
                  <ul className="briefing-list">
                    {searchResults.briefing.map((b, idx) => (
                      <li key={idx} className="briefing-item" onClick={() => { setSelectedDate(b.date); clearSearch(); }}>
                        <span className="date">{formatDate(b.date)}</span>
                        <span className="summary">{b.summary[0]?.substring(0, 100)}...</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

          </div>
        ) : (
          <>
            {/* 날짜 네비게이션 */}
            <div className="date-navigator" ref={dateDropdownRef}>
              {loading ? (
                <div className="loading">불러오는 중...</div>
              ) : dates.length === 0 ? (
                <div className="empty">저장된 기록이 없습니다</div>
              ) : (
                <div className="date-nav-controls">
                  {/* 이전 날짜 (과거) */}
                  <button
                    className="date-nav-btn prev"
                    onClick={goToPrevDate}
                    disabled={currentDateIndex >= dates.length - 1}
                    title="이전 날짜"
                  >
                    <Icon name="chevronLeft" size={20} />
                  </button>

                  {/* 현재 날짜 (클릭 시 드롭다운) */}
                  <button
                    className="date-current"
                    onClick={() => setDateDropdownOpen(!dateDropdownOpen)}
                  >
                    <span className="date-text">
                      {selectedDate ? formatDate(selectedDate) : '날짜 선택'}
                    </span>
                    {selectedDate && dates[currentDateIndex] && (
                      <span className="date-count">{getDateItemCount(dates[currentDateIndex])}</span>
                    )}
                    <Icon name="chevronDown" size={16} className={`dropdown-icon ${dateDropdownOpen ? 'open' : ''}`} />
                  </button>

                  {/* 다음 날짜 (최신) */}
                  <button
                    className="date-nav-btn next"
                    onClick={goToNextDate}
                    disabled={currentDateIndex <= 0}
                    title="다음 날짜"
                  >
                    <Icon name="chevronRight" size={20} />
                  </button>

                  {/* 날짜 드롭다운 */}
                  {dateDropdownOpen && (
                    <div className="date-dropdown">
                      <ul>
                        {dates.map((item, idx) => (
                          <li
                            key={item.date}
                            className={selectedDate === item.date ? 'selected' : ''}
                            onClick={() => {
                              setSelectedDate(item.date);
                              setDateDropdownOpen(false);
                            }}
                          >
                            <span className="date">{formatDateShort(item.date)}</span>
                            <span className="count">{getDateItemCount(item)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 상세 내용 */}
            <div className="detail-view">
              {!selectedDate ? (
                <div className="empty-detail">
                  <Icon name="calendar" size={48} />
                  <p>날짜를 선택하세요</p>
                </div>
              ) : detailLoading ? (
                <div className="loading">불러오는 중...</div>
              ) : (
                <>
                  {/* 뉴스 상세 */}
                  {activeTab === 'news' && (
                    <div className="news-detail">
                      <h3>{formatDate(selectedDate)} 뉴스 ({articles.length}건)</h3>

                      {/* 뷰 모드 탭 */}
                      <div className="news-view-tabs">
                        <button
                          className={`view-tab-btn ${newsViewMode === 'list' ? 'active' : ''}`}
                          onClick={() => setNewsViewMode('list')}
                        >
                          전체 목록
                        </button>
                        <button
                          className={`view-tab-btn ${newsViewMode === 'category' ? 'active' : ''}`}
                          onClick={() => setNewsViewMode('category')}
                        >
                          카테고리별
                        </button>
                      </div>

                      {articles.length === 0 ? (
                        <p className="empty">뉴스가 없습니다</p>
                      ) : newsViewMode === 'list' ? (
                        /* 전체 목록 - 테이블 형태 */
                        <table className="news-table">
                          <thead>
                            <tr>
                              <th className="col-num">#</th>
                              <th className="col-category">카테고리</th>
                              <th className="col-title">제목</th>
                              <th className="col-source">출처</th>
                            </tr>
                          </thead>
                          <tbody>
                            {articles.map((article, idx) => (
                              <tr key={article.id}>
                                <td className="col-num">{idx + 1}</td>
                                <td className="col-category">
                                  <span className="category-badge">{getCategoryName(article.category)}</span>
                                </td>
                                <td className="col-title">
                                  <a href={article.link} target="_blank" rel="noopener noreferrer">
                                    {article.title}
                                  </a>
                                </td>
                                <td className="col-source">{article.source}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        /* 카테고리별 그룹화 */
                        <div className="news-category-groups">
                          {newsCategories.map((cat, idx) => {
                            const isExpanded = expandedNewsCategory === cat.category;
                            return (
                              <div
                                key={cat.category}
                                id={`news-cat-${cat.category}`}
                                className={`news-category-card ${isExpanded ? 'expanded' : ''}`}
                              >
                                <div
                                  className="news-category-header"
                                  onClick={() => {
                                    const newExpanded = isExpanded ? null : cat.category;
                                    setExpandedNewsCategory(newExpanded);
                                    if (newExpanded) {
                                      setTimeout(() => {
                                        const element = document.getElementById(`news-cat-${cat.category}`);
                                        if (element) {
                                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                      }, 100);
                                    }
                                  }}
                                >
                                  <span className="cat-rank">#{idx + 1}</span>
                                  <span className="cat-name">{getCategoryName(cat.category)}</span>
                                  <span className="cat-count">{cat.count}건</span>
                                  <span className="cat-toggle">{isExpanded ? '▲' : '▼'}</span>
                                </div>
                                {isExpanded && (
                                  <div className="news-category-articles">
                                    <table className="news-table compact">
                                      <thead>
                                        <tr>
                                          <th className="col-num">#</th>
                                          <th className="col-title">제목</th>
                                          <th className="col-source">출처</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {cat.articles.map((article, aIdx) => (
                                          <tr key={article.id}>
                                            <td className="col-num">{aIdx + 1}</td>
                                            <td className="col-title">
                                              <a href={article.link} target="_blank" rel="noopener noreferrer">
                                                {article.title}
                                              </a>
                                            </td>
                                            <td className="col-source">{article.source}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 브리핑 통합 상세 */}
                  {activeTab === 'briefing' && (
                    <div className="briefing-unified-detail">
                      <h3>{formatDate(selectedDate)} AI 브리핑</h3>

                      {!hasBriefingData ? (
                        <p className="empty">해당 날짜의 브리핑이 없습니다</p>
                      ) : (
                        <>
                          {/* 브리핑 내부 탭 */}
                          <div className="briefing-view-tabs">
                            {briefingData.daily && (
                              <button
                                className={`view-tab ${briefingViewTab === 'daily' ? 'active' : ''}`}
                                onClick={() => setBriefingViewTab('daily')}
                              >
                                <span className="tab-icon">📊</span>
                                <span className="tab-label">일일 브리핑</span>
                                <span className="tab-badge">{briefingData.daily.totalArticles}건</span>
                              </button>
                            )}
                            {briefingData.themes && (
                              <button
                                className={`view-tab ${briefingViewTab === 'themes' ? 'active' : ''}`}
                                onClick={() => setBriefingViewTab('themes')}
                              >
                                <span className="tab-icon">🔥</span>
                                <span className="tab-label">테마별 이슈</span>
                                <span className="tab-badge">{briefingData.themes.themes?.length || 0}개</span>
                              </button>
                            )}
                            <button
                              className={`view-tab ${briefingViewTab === 'audio' ? 'active' : ''}`}
                              onClick={() => setBriefingViewTab('audio')}
                            >
                              <span className="tab-icon">🎙️</span>
                              <span className="tab-label">오디오</span>
                              <span className="tab-badge">
                                {briefingData.audioBriefings.length > 0
                                  ? `${briefingData.audioBriefings.length}/2`
                                  : '준비중'}
                              </span>
                            </button>
                          </div>

                          {/* 탭 컨텐츠 */}
                          <div className="briefing-tab-content">
                            {/* 일일 브리핑 */}
                            {briefingViewTab === 'daily' && briefingData.daily && (
                              <div className="daily-content">
                                {/* AI Insight */}
                                {briefingData.daily.aiInsight && (
                                  <div className="ai-insight">
                                    <h5><Icon name="chart" size={16} /> AI 시장 분석</h5>
                                    <pre className="insight-text">{briefingData.daily.aiInsight}</pre>
                                  </div>
                                )}

                                {/* 주요 요약 */}
                                {briefingData.daily.summary && briefingData.daily.summary.length > 0 && (
                                  <div className="summary-list">
                                    <h5>주요 요약</h5>
                                    <ul>
                                      {briefingData.daily.summary.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {/* 핫 키워드 */}
                                {briefingData.daily.hotKeywords?.length > 0 && (
                                  <div className="keywords">
                                    <h5><Icon name="fire" size={16} /> 핫 키워드</h5>
                                    <div className="keyword-tags">
                                      {briefingData.daily.hotKeywords.slice(0, 10).map((k, idx) => (
                                        <span key={idx} className="tag">{k.word} ({k.count})</span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* 섹터별 동향 */}
                                {briefingData.daily.sectorTrend && Object.keys(briefingData.daily.sectorTrend).length > 0 && (
                                  <div className="sector-trend">
                                    <h5><Icon name="trendingUp" size={16} /> 섹터별 동향</h5>
                                    <div className="sector-grid">
                                      {Object.entries(briefingData.daily.sectorTrend).map(([sector, data]) => (
                                        <div key={sector} className={`sector-item status-${data.status}`}>
                                          <span className="sector-name">{sector}</span>
                                          <span className="sector-label">{data.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* 테마별 이슈 */}
                            {briefingViewTab === 'themes' && briefingData.themes && (
                              <div className="themes-content">
                                {/* AI 핵심 요약 */}
                                {briefingData.themes.overallSummary && briefingData.themes.overallSummary.length > 0 && (
                                  <div className="overall-summary">
                                    <h5><Icon name="chart" size={16} /> 핵심 요약</h5>
                                    <ul>
                                      {briefingData.themes.overallSummary.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {/* 테마 목록 */}
                                {briefingData.themes.themes && briefingData.themes.themes.length > 0 && (
                                  <div className="theme-list">
                                    {briefingData.themes.themes.map((theme, idx) => (
                                      <div key={idx} className="theme-card">
                                        <div className="theme-header">
                                          <span className="theme-rank">#{idx + 1}</span>
                                          <span className="theme-keyword">{theme.keyword}</span>
                                          <span className="theme-count">{theme.articleCount}건</span>
                                        </div>
                                        <p className="theme-summary">{theme.issueSummary}</p>
                                        {theme.relatedArticles && theme.relatedArticles.length > 0 && (
                                          <ul className="related-articles">
                                            {theme.relatedArticles.slice(0, 3).map((article, aIdx) => (
                                              <li key={aIdx}>
                                                <a href={article.link} target="_blank" rel="noopener noreferrer">
                                                  {article.title}
                                                </a>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* 오디오 브리핑 */}
                            {briefingViewTab === 'audio' && (
                              <div className="audio-content">
                                {/* 아침/저녁 선택 탭 - 항상 표시 */}
                                <div className="audio-slot-tabs">
                                  {['morning', 'evening'].map(slot => {
                                    const audio = briefingData.audioBriefings.find(a => a.timeSlot === slot);
                                    const isReady = audio && audio.audioUrl;
                                    return (
                                      <button
                                        key={slot}
                                        className={`audio-slot-tab ${selectedAudioSlot === slot ? 'active' : ''} ${!isReady ? 'pending' : ''}`}
                                        onClick={() => {
                                          if (audioRef.current) {
                                            audioRef.current.pause();
                                            setIsPlaying(false);
                                          }
                                          setSelectedAudioSlot(slot);
                                          setAudioTime({ current: 0, duration: 0 });
                                        }}
                                      >
                                        {slot === 'evening' ? '🌙 저녁' : '☀️ 아침'}
                                        {!isReady && <span className="pending-badge">준비중</span>}
                                      </button>
                                    );
                                  })}
                                </div>

                                {currentAudio && currentAudio.audioUrl ? (
                                  <div className="audio-player-section">
                                    <div className="audio-info-header">
                                      <span className={`time-slot-badge ${currentAudio.timeSlot || 'morning'}`}>
                                        {currentAudio.timeSlot === 'evening' ? '🌙 저녁 브리핑' : '☀️ 아침 브리핑'}
                                      </span>
                                      {currentAudio.fileSize && (
                                        <span className="file-size">
                                          {Math.round(currentAudio.fileSize / 1024 / 1024 * 10) / 10}MB
                                        </span>
                                      )}
                                    </div>

                                    <div className="audio-controls">
                                      <audio
                                        ref={audioRef}
                                        src={`${API_BASE_URL}${currentAudio.audioUrl}`}
                                        onTimeUpdate={handleAudioTimeUpdate}
                                        onEnded={() => setIsPlaying(false)}
                                        onPlay={() => setIsPlaying(true)}
                                        onPause={() => setIsPlaying(false)}
                                      />

                                      <button
                                        className={`play-btn ${isPlaying ? 'playing' : ''}`}
                                        onClick={handleAudioPlay}
                                      >
                                        {isPlaying ? <Icon name="pause" size={24} /> : <Icon name="play" size={24} />}
                                      </button>

                                      <div className="progress-container" onClick={handleAudioSeek}>
                                        <div className="progress-bar">
                                          <div
                                            className="progress-fill"
                                            style={{
                                              width: audioTime.duration ? `${(audioTime.current / audioTime.duration) * 100}%` : '0%'
                                            }}
                                          />
                                        </div>
                                      </div>

                                      <div className="time-display">
                                        <span>{formatAudioTime(audioTime.current)}</span>
                                        <span>/</span>
                                        <span>{formatAudioTime(audioTime.duration)}</span>
                                      </div>
                                    </div>

                                    {currentAudio.script && (
                                      <div className="script-section">
                                        <h5>📝 브리핑 대본</h5>
                                        <pre className="script-text">{currentAudio.script}</pre>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="audio-pending-notice">
                                    <div className="pending-icon">🎙️</div>
                                    <p className="pending-title">
                                      {selectedAudioSlot === 'evening' ? '저녁 브리핑 준비 중' : '아침 브리핑 준비 중'}
                                    </p>
                                    <p className="pending-desc">
                                      {selectedAudioSlot === 'evening'
                                        ? '저녁 6시경 공개 예정입니다.'
                                        : '오전 9시경 공개 예정입니다.'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Archive;
